import { useMemo, useState } from 'react';
import { useCommunityPicker } from '../lib/useCommunityPicker';
import { useCommunityFilters } from '../lib/useCommunityFilters';
import { canModerateCommunity } from '../lib/canModerateCommunity';
import { CommunityProvider } from '../lib/communityContext';
import { useCommunityCreatorIdentity } from '../api/useCommunityCreatorIdentity';
import { useIsDmFeatureEnabled } from '../api/useIsDmFeatureEnabled';
import { useIsContentEditEnabled } from '../lib/useIsContentEditEnabled';
import { Community } from '../model/community.types';
import { CommunitiesHeader } from './CommunitiesHeader';
import { CommunityFeed } from './CommunityFeed';
import { CommunityModerationPanel } from './CommunityModerationPanel';
import { CommunityModerationSummary } from './CommunityModerationSummary';
import { CommunityToolbar } from './CommunityToolbar';
import { FeedFilterToggle } from './FeedFilterToggle';
import { FeedSearch } from './FeedSearch';
import { NewPostField } from './NewPostField';
import { PinnedPostsSection } from './PinnedPostsSection';

type Props = {
  communities: Community[];
  influencerId?: string;
};

export const CommunitiesContent = ({ communities, influencerId }: Props) => {
  const communityIds = useMemo(
    () => communities.map((community) => community.id),
    [communities],
  );
  const { activeId } = useCommunityPicker(communityIds);
  const canModerate = canModerateCommunity(influencerId);
  const isDmFeatureEnabled = useIsDmFeatureEnabled(influencerId);
  const isContentEditEnabled = useIsContentEditEnabled(influencerId);
  const creatorIdentity = useCommunityCreatorIdentity(influencerId);
  const activeCommunity = communities.find(
    (community) => community.id === activeId,
  );

  const [isModerationOpen, setModerationOpen] = useState(false);
  const { filter, changeFilter, search, setSearch, debouncedSearch } =
    useCommunityFilters(activeId);

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-2">
      <CommunitiesHeader onModeration={() => setModerationOpen(true)} />
      {activeCommunity && (
        <CommunityProvider
          communityId={activeCommunity.id}
          influencerId={influencerId}
          canModerate={canModerate}
          isDmFeatureEnabled={isDmFeatureEnabled}
          isContentEditEnabled={isContentEditEnabled}
        >
          <div className="flex flex-col gap-7">
            <CommunityToolbar membersCount={activeCommunity.membersCount} />
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                  <FeedFilterToggle value={filter} onChange={changeFilter} />
                  <FeedSearch value={search} onChange={setSearch} />
                </div>
                <PinnedPostsSection />
                {canModerate && (
                  <NewPostField
                    author={creatorIdentity}
                    memberCount={activeCommunity.membersCount}
                  />
                )}
                <CommunityFeed filter={filter} search={debouncedSearch} />
              </div>
              <CommunityModerationSummary community={activeCommunity} />
            </div>
          </div>
        </CommunityProvider>
      )}
      <CommunityModerationPanel
        communities={communities}
        isOpen={isModerationOpen}
        onClose={() => setModerationOpen(false)}
      />
    </div>
  );
};
